import { rupiah } from "@/lib/format";
import { CopyValue } from "@/components/spt/CopyValue";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export function Amount({ v, testid, label }) {
  return (
    <span className="inline-flex items-center gap-2 justify-end">
      <span className="font-mono tabular-nums">{rupiah(v)}</span>
      <CopyValue value={v} label={label} testid={testid}/>
    </span>
  );
}

export function SptTable({ head, rows, empty, testid }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md" data-testid={testid}>
      <table className="w-full text-sm min-w-[560px]">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>{head.map((h, i) => <th key={h} className={`p-2.5 ${i === head.length - 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length ? rows : <tr><td colSpan={head.length} className="p-4 text-center text-slate-400 text-[13px]">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function TotalRow({ label, v, testid }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-[#166db4]/5 border border-[#166db4]/15 px-3 py-2.5">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <Amount v={v} testid={testid} label={label}/>
    </div>
  );
}

const Td = ({ children, right }) => <td className={`p-2.5 border-t border-slate-100 ${right ? "text-right" : ""}`}>{children}</td>;

export function TabInduk({ d }) {
  const tone = d.status === "Kurang Bayar" ? "bg-rose-50 border-rose-200 text-rose-800" : d.status === "Lebih Bayar" ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-emerald-50 border-emerald-200 text-emerald-800";
  return <div className="space-y-3">
    <div className={`rounded-md border p-3 flex items-center justify-between ${tone}`} data-testid="spt-induk-status">
      <span className="text-sm">Status SPT</span><span className="text-lg font-semibold">{d.status}</span>
    </div>
    <TotalRow label="Penghasilan Neto" v={d.penghasilan_neto} testid="spt-induk-neto"/>
    <TotalRow label="Zakat / Sumbangan Wajib" v={d.zakat} testid="spt-induk-zakat"/>
    <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
      <span className="text-sm font-semibold text-slate-700">PTKP <span className="font-mono text-[12px] text-slate-500" data-testid="spt-induk-status-ptkp">({d.status_ptkp})</span></span>
      <Amount v={d.ptkp} testid="spt-induk-ptkp" label="PTKP"/>
    </div>
    <TotalRow label="Penghasilan Kena Pajak (PKP)" v={d.pkp} testid="spt-induk-pkp"/>
    <TotalRow label="PPh Terutang" v={d.pph_terutang} testid="spt-induk-pph"/>
    <TotalRow label="Kredit Pajak (dipotong/dibayar)" v={d.kredit_pajak} testid="spt-induk-kredit"/>
    <TotalRow label={d.pph_lebih_bayar > 0 ? "PPh Lebih Bayar" : "PPh Kurang Bayar"} v={d.pph_lebih_bayar > 0 ? d.pph_lebih_bayar : d.pph_kurang_bayar} testid="spt-induk-kb"/>
    <p className="text-[12px] text-slate-500">Info pendukung: PPh final {rupiah(d.pph_final)} · bukan objek {rupiah(d.bukan_objek)} · harta {rupiah(d.harta)} · utang {rupiah(d.utang)}.</p>
  </div>;
}

export function TabL1A({ d }) {
  return <div className="space-y-3">
    <SptTable testid="spt-l1a-table" head={["Kode", "Nama Harta", "Th. Perolehan", "Harga Perolehan"]} empty="Belum ada harta di tahun ini (menu Daftar Harta)."
      rows={d.rows.map((r, i) => <tr key={i}><Td>{r.kode}</Td><Td>{r.nama}</Td><Td>{r.tahun_perolehan || "-"}</Td><Td right><Amount v={r.nilai} testid={`spt-l1a-copy-${i}`} label={r.nama}/></Td></tr>)}/>
    <TotalRow label="Jumlah Harta" v={d.total} testid="spt-l1a-total"/>
  </div>;
}

export function TabL1B({ d }) {
  return <div className="space-y-3">
    <SptTable testid="spt-l1b-table" head={["Kode", "Pemberi Pinjaman", "Th. Peminjaman", "Jumlah"]} empty="Belum ada utang di tahun ini (menu Daftar Utang)."
      rows={d.rows.map((r, i) => <tr key={i}><Td>{r.kode}</Td><Td>{r.pemberi}{r.nama ? ` — ${r.nama}` : ""}</Td><Td>{r.tahun_peminjaman || "-"}</Td><Td right><Amount v={r.nilai} testid={`spt-l1b-copy-${i}`} label={r.pemberi}/></Td></tr>)}/>
    <TotalRow label="Jumlah Utang" v={d.total} testid="spt-l1b-total"/>
  </div>;
}

export function TabGrouped({ d, id, head, empty, totalLabel }) {
  return <div className="space-y-3">
    <SptTable testid={`spt-${id}-table`} head={head} empty={empty}
      rows={d.rows.map((r, i) => <tr key={i}><Td>{r.uraian}</Td><Td right><Amount v={r.jumlah} testid={`spt-${id}-copy-${i}`} label={r.uraian}/></Td></tr>)}/>
    <TotalRow label={totalLabel} v={d.total} testid={`spt-${id}-total`}/>
  </div>;
}

export function TabL2A({ d }) {
  return <div className="space-y-3">
    <SptTable testid="spt-l2a-table" head={["Jenis Penghasilan", "Penghasilan Bruto", "PPh Final Terutang"]} empty="Belum ada pemasukan dengan perlakuan pajak Final."
      rows={d.rows.map((r, i) => <tr key={i}><Td>{r.uraian}</Td><Td right><Amount v={r.bruto} testid={`spt-l2a-bruto-${i}`} label="Bruto"/></Td><Td right><Amount v={r.pph_final} testid={`spt-l2a-pph-${i}`} label="PPh Final"/></Td></tr>)}/>
    <TotalRow label="Total Penghasilan Bruto" v={d.total_bruto} testid="spt-l2a-total-bruto"/>
    <TotalRow label="Total PPh Final" v={d.total_pph} testid="spt-l2a-total-pph"/>
  </div>;
}

export function TabL3BA({ d }) {
  return <div className="space-y-3">
    <p className="text-[12px] text-slate-500">Tarif {d.tarif_persen}% · omzet s.d. {rupiah(d.batas_bebas)} per tahun tidak dikenai PPh final · setor paling lambat tanggal 15 bulan berikutnya, kode setor (KAP-KJS) <span className="font-mono">{d.kode_setor}</span> <CopyValue raw value={d.kode_setor} label="Kode setor" testid="spt-l3ba-kode"/></p>
    <SptTable testid="spt-l3ba-table" head={["Bulan", "Peredaran Bruto", "PPh Final", "Sudah Disetor"]} empty=""
      rows={d.rows.map((r) => <tr key={r.bulan}><Td>{BULAN[r.bulan - 1]}<div className="text-[10px] text-slate-400">jatuh tempo {r.jatuh_tempo}</div></Td><Td right><Amount v={r.omzet} testid={`spt-l3ba-omzet-${r.bulan}`} label={`Omzet ${BULAN[r.bulan - 1]}`}/></Td><Td right><Amount v={r.pph_final} testid={`spt-l3ba-pph-${r.bulan}`} label={`PPh ${BULAN[r.bulan - 1]}`}/></Td><Td right><Amount v={r.disetor} testid={`spt-l3ba-setor-${r.bulan}`} label={`Disetor ${BULAN[r.bulan - 1]}`}/></Td></tr>)}/>
    <TotalRow label="Total Peredaran Bruto" v={d.total_omzet} testid="spt-l3ba-total-omzet"/>
    <TotalRow label="Total PPh Final" v={d.total_pph_final} testid="spt-l3ba-total-pph"/>
    <TotalRow label="Total PPh Final Sudah Disetor" v={d.total_disetor} testid="spt-l3ba-total-setor"/>
  </div>;
}

export function TabL1D({ d }) {
  return <div className="space-y-3">
    <SptTable testid="spt-l1d-table" head={["Pemberi Kerja", "Penghasilan Bruto"]} empty="Belum ada pemasukan jenis Gaji (perlakuan progresif)."
      rows={d.rows.map((r, i) => <tr key={i}><Td>{r.pemberi_kerja}</Td><Td right><Amount v={r.bruto} testid={`spt-l1d-copy-${i}`} label={r.pemberi_kerja}/></Td></tr>)}/>
    <TotalRow label="Penghasilan Bruto" v={d.bruto} testid="spt-l1d-bruto"/>
    <TotalRow label="Biaya Jabatan" v={d.biaya_jabatan} testid="spt-l1d-bj"/>
    <TotalRow label="Penghasilan Neto" v={d.neto} testid="spt-l1d-neto"/>
    <TotalRow label="PPh 21 Dipotong" v={d.pph21_dipotong} testid="spt-l1d-pph21"/>
  </div>;
}

export function TabL3A2({ d }) {
  return <div className="space-y-3">
    <TotalRow label="Peredaran Usaha (Bruto)" v={d.peredaran_usaha} testid="spt-l3a2-bruto"/>
    <SptTable testid="spt-l3a2-table" head={["Biaya Usaha", "Jumlah"]} empty="Belum ada pengeluaran yang ditandai pengurang bruto."
      rows={d.biaya.map((r, i) => <tr key={i}><Td>{r.uraian}</Td><Td right><Amount v={r.jumlah} testid={`spt-l3a2-copy-${i}`} label={r.uraian}/></Td></tr>)}/>
    <TotalRow label="Total Biaya" v={d.total_biaya} testid="spt-l3a2-biaya"/>
    <TotalRow label="Laba Neto Komersial" v={d.laba_neto_komersial} testid="spt-l3a2-komersial"/>
    <TotalRow label="Koreksi Fiskal" v={d.koreksi_fiskal} testid="spt-l3a2-koreksi"/>
    <TotalRow label="Penghasilan Neto Fiskal" v={d.laba_neto_fiskal} testid="spt-l3a2-fiskal"/>
  </div>;
}
